import styled, { css } from 'styled-components';

import { Z_INDEX_VERTICAL_MENU } from '@savgroup-front-common/constants/shared';
import {
  rgba,
  setLightness,
} from '@savgroup-front-common/primitive/helpers/cssColor';
import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { rem } from '@savgroup-front-common/primitive/helpers/units';
import { SUPPORTED_PROPERTIES } from '@savgroup-front-common/types';

import { SafeMotionDiv } from '../../animation/SafeMotionDiv';
import { SafeMotionUl } from '../../animation/SafeMotionUl';
import { styledGetCustomComponentProperty } from '../../helpers';
import { fontStyle } from '../../theme/helpers/fontStyle';
import { Link } from '../link';

import { MENU_POSITIONS } from './Menu.types';

const disabledCss = css`
  color: ${({ theme }) => theme.colors.paragraphTextColor}!important;
  background-color: transparent !important;
  cursor: not-allowed;
`;

interface MenuItemMixinProps {
  $color?: string;
  disabled?: boolean;
  $isDisabled?: boolean;
  $hasSecondaryAction?: boolean;
  $isActive?: boolean;
  $minWidth?: number;
}

export const $MenuContent = styled.span`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${spacing('sm')};
  min-width: 0;
`;

export const $MenuTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const $Description = styled.div`
  font-weight: ${fontWeight('normal')};
  ${fontStyle('xs')};
  color: ${({ theme }) => theme.colors.paragraphTextColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const menuItemMixin = css<MenuItemMixinProps>`
  padding: ${spacing(1.5)} ${spacing('md')} ${spacing(1.5)} ${spacing('sm')};
  text-align: left;
  color: ${({ theme, $color }) => {
    if ($color) {
      return $color;
    }

    return theme.colors.mainTextColor;
  }};
  background-color: transparent;
  width: 100%;
  height: unset;
  ${fontStyle('sm')};
  border: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: ${fontWeight('medium')};
  outline: 0;
  white-space: nowrap;
  min-width: ${({ $minWidth }) => $minWidth || rem(196)};
  cursor: pointer;

  svg {
    color: ${({ theme, $color, disabled, $isDisabled }) => {
      if (disabled || $isDisabled) {
        return theme.colors.paragraphTextColor;
      }

      return $color ?? theme.newUI?.defaultColors?.secondary;
    }};
  }

  flex-direction: row;
  gap: ${spacing('sm')};

  &:focus,
  &:hover {
    color: ${({ theme, $color }) => {
      return $color ?? theme.colors.mainTextColor;
    }};
    text-decoration: none;
    background-color: ${({ theme }) => {
      return theme.newUI.variationColors.light6;
    }};
  }
  &:active {
    background-color: ${({ theme, $color }) =>
      rgba(setLightness(0.7, $color || theme.colors.darkBackground), 0.5)};
  }
  ${({ disabled, $isDisabled }) => (disabled || $isDisabled) && disabledCss}
`;

export const $MenuButtonContainer = styled(SafeMotionDiv)<MenuItemMixinProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: ${spacing('xs')};

  background-color: ${({ theme, $isActive }) => {
    if (!$isActive) {
      return undefined;
    }

    return theme.newUI.variationColors.light5;
  }};

  &:hover {
    color: ${({ theme, $color }) => {
      if ($color) {
        return setLightness(0.75, $color);
      }

      return theme.colors.mainTextColor;
    }};
    text-decoration: none;
    background-color: ${({ theme }) => {
      return theme.newUI.variationColors.light6;
    }};
  }

  & button:hover {
    background-color: transparent;
  }
`;

export const $MenuItem = styled.div<MenuItemMixinProps>`
  ${menuItemMixin}
`;
export const $MenuButton = styled.button<MenuItemMixinProps>`
  ${menuItemMixin}
`;
export const $MenuBack = styled.button<MenuItemMixinProps>`
  ${menuItemMixin};
  font-weight: ${fontWeight('bold')};
  border-bottom: ${spacing('px')} solid #666;
`;

export const $MenuSubMenu = styled.button<MenuItemMixinProps>`
  ${menuItemMixin};
  justify-content: space-between;
  svg:last-child {
    margin-right: -${spacing(2.5)};
  }
`;

export const $MenuLink = styled(Link)<
  React.PropsWithChildren<MenuItemMixinProps>
>`
  ${menuItemMixin}
`;

interface MenuTransitionProps {
  $position?: MENU_POSITIONS | { top: string; left: string };
  $componentThemeName?: string;
  $minWidth?: number;
}

const getMenuTransitionPadding = ({
  $componentThemeName,
}: MenuTransitionProps) => {
  if ($componentThemeName) {
    return styledGetCustomComponentProperty({
      componentName: $componentThemeName,
      property: SUPPORTED_PROPERTIES.PADDING,
    });
  }

  return spacing(3.5);
};

export const $MenuTransition = styled(SafeMotionUl)<MenuTransitionProps>`
  position: absolute;
  list-style: none;
  border-radius: ${({ $componentThemeName, theme }) => {
    if ($componentThemeName) {
      return styledGetCustomComponentProperty({
        componentName: $componentThemeName,
        property: SUPPORTED_PROPERTIES.BORDER_RADIUS,
      });
    }

    return theme.newUI.borders.bigRadius;
  }};

  background: ${({ $componentThemeName, theme }) => {
    if ($componentThemeName) {
      return styledGetCustomComponentProperty({
        componentName: $componentThemeName,
        property: SUPPORTED_PROPERTIES.BACKGROUND_COLOR,
      });
    }

    return theme.systemColors.defaultBackground;
  }};

  color: ${({ $componentThemeName, theme }) => {
    if ($componentThemeName) {
      return styledGetCustomComponentProperty({
        componentName: $componentThemeName,
        property: SUPPORTED_PROPERTIES.COLOR,
      });
    }

    return theme.newUI.defaultColors.text;
  }};

  min-width: ${({ $minWidth }) => ($minWidth ? `${$minWidth}px` : '100%')};
  max-width: 480px;
  max-height: 455px;
  overflow: hidden;
  overflow-y: auto;

  margin-top: ${spacing('xxs')};
  display: flex;
  flex-direction: column;
  padding: ${getMenuTransitionPadding};
  /* Browsers ship a UA default padding-inline-start of 40px on ul elements. It wins over the
     shorthand above for the inline-start side specifically, so it needs its own override. */
  padding-inline-start: ${getMenuTransitionPadding} !important;

  min-height: ${spacing(10)};
  pointer-events: auto;

  border: ${({ $componentThemeName }) =>
    $componentThemeName
      ? styledGetCustomComponentProperty({
          componentName: $componentThemeName,
          property: SUPPORTED_PROPERTIES.BORDER,
        })
      : null};

  gap: ${spacing(1.5)};
  ${({ $position }) => {
    if ($position && typeof $position === 'object') {
      return css`
        top: ${$position.top};
        left: ${$position.left};
      `;
    }

    switch ($position) {
      case MENU_POSITIONS.TOP:
        return css`
          top: 0;
        `;
      case MENU_POSITIONS.BOTTOM:
        return css`
          top: 200%;
        `;
      case MENU_POSITIONS.LEFT:
        return css`
          left: 0;
        `;
      case MENU_POSITIONS.RIGHT:
        return css`
          right: 0;
        `;

      case MENU_POSITIONS.TOP_RIGHT:
        return css`
          bottom: 100%;
          top: auto;
          right: 0;
          margin-top: 0;
          margin-bottom: ${spacing('xxs')};
        `;

      default: {
        return null;
      }
    }
  }};
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);
`;

export const $MenuTitle = styled.h6`
  padding: 0 ${spacing('md')} 0 ${spacing('sm')};
  width: 100%;
  color: ${({ theme }) => theme.newUI.defaultColors.text};

  ${fontStyle('xs')};
  font-weight: ${fontWeight('semibold')};
`;

export const $MenuItemStyled = styled.li<{
  $minWidth?: number;
}>`
  width: 100%;
  min-width: ${({ $minWidth }) => $minWidth || '275px'};
`;

interface $MenuContainerProps {
  $position?: MENU_POSITIONS | { top: string; left: string };
}

export const $MenuContainer = styled.div<$MenuContainerProps>`
  pointer-events: none;
  position: relative;
  z-index: ${Z_INDEX_VERTICAL_MENU};
  ${({ $position }) => {
    switch ($position) {
      case MENU_POSITIONS.BOTTOM:
        return css`
          right: 100%;
        `;

      default: {
        return null;
      }
    }
  }};
`;
